import React, { useState, useEffect, useCallback } from "react";
import { C, TRANSACTION_TYPES, TRANSACTION_STATUSES, PAYMENT_METHODS, STRATEGIC_PLAN, COLOR_TEMPLATES } from "./constants";
import {
  X, Plus, FileText, Calendar, DollarSign, TrendingUp,
  CheckCircle, Clock, AlertCircle, XCircle,
  Download, Users, Target, Paperclip,
  ArrowLeft, Save,
  Layers, CreditCard, Receipt
} from "lucide-react";

// ===== useToast helper =====
export function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);
  const ToastView = toast ? (
    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
  ) : null;
  return [showToast, ToastView];
}

// ===== Card =====
export function Card({ children, style, className = "", onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={className}
      style={{
        background: C.white,
        borderRadius: 16,
        padding: 24,
        border: `1px solid ${C.border}`,
        boxShadow: hover
          ? "0 4px 16px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ===== Btn =====
export function Btn({ children, onClick, variant = "primary", size = "md", icon: Icon, disabled, style, type = "button" }) {
  const variants = {
    primary: { bg: C.accent, color: C.primary, hoverBg: "#c9ad4e", border: "none" },
    danger:  { bg: C.danger,  color: "#fff",    hoverBg: "#a83e2e", border: "none" },
    ghost:   { bg: "transparent", color: C.primary, hoverBg: "#f4f5f7", border: `1px solid ${C.border}` },
    success: { bg: C.success, color: "#fff",    hoverBg: "#236e47", border: "none" },
    teal:    { bg: C.teal,    color: "#fff",    hoverBg: "#009688", border: "none" },
    outline: { bg: "transparent", color: C.primary, hoverBg: "#f4f5f7", border: `1px solid ${C.border}` },
  };
  const v = variants[variant] || variants.primary;
  const sizes = {
    sm: { padding: "7px 14px",  fontSize: 12 },
    md: { padding: "10px 20px", fontSize: 14 },
    lg: { padding: "13px 26px", fontSize: 15 },
  };
  const s = sizes[size] || sizes.md;

  const [hover, setHover] = useState(false);

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: disabled ? "#e8e8e8" : hover ? v.hoverBg : v.bg,
        color: disabled ? "#999" : v.color,
        border: v.border,
        borderRadius: 10,
        fontFamily: "Readex Pro, sans-serif",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        ...s,
        ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 14 : size === "lg" ? 18 : 16} />}
      {children}
    </button>
  );
}

// ===== Field =====
export function Field({ label, type = "text", value, onChange, options, placeholder, required, rows = 3, disabled, style, min, max, step }) {
  const base = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    fontFamily: "Readex Pro, sans-serif",
    fontSize: 14,
    color: C.text,
    background: disabled ? "#f5f5f5" : C.white,
    direction: "rtl",
    transition: "border-color 0.15s, box-shadow 0.15s",
    outline: "none",
  };

  return (
    <div style={{ marginBottom: 16, ...style }}>
      {label && (
        <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textLight }}>
          {label} {required && <span style={{ color: C.danger }}>*</span>}
        </label>
      )}
      {type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={{ ...base, cursor: "pointer" }}>
          <option value="">{placeholder || "اختر..."}</option>
          {(options || []).map((o) => (
            <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled} style={{ ...base, resize: "vertical" }} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} min={min} max={max} step={step} style={base} />
      )}
    </div>
  );
}

// ===== Toast =====
export function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = { success: C.success, error: C.danger, warning: C.warning, info: C.teal };
  const icons = { success: CheckCircle, error: XCircle, warning: AlertCircle, info: AlertCircle };
  const Icon = icons[type];

  return (
    <div
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: colors[type],
        color: "#fff",
        padding: "14px 28px",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        zIndex: 9999,
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        animation: "fadeIn 0.3s ease-out",
        fontWeight: 600,
        fontSize: 14,
      }}
    >
      <Icon size={18} />
      {message}
      <X size={16} style={{ cursor: "pointer", opacity: 0.7, marginRight: 8 }} onClick={onClose} />
    </div>
  );
}

// ===== ModuleHeader =====
export function ModuleHeader({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 4px 12px ${C.primary}25`,
      }}>
        <Icon size={28} color="#fff" />
      </div>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: C.primary, margin: 0, lineHeight: 1.3 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: C.textLight, margin: "4px 0 0", lineHeight: 1.6 }}>{subtitle}</p>}
      </div>
    </div>
  );
}

// ===== KPICard =====
export function KPICard({ title, value, icon: Icon, color = C.teal, subtitle, trend }) {
  return (
    <Card style={{ minHeight: 120, padding: 22, position: "relative", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, color: C.textLight, margin: 0, marginBottom: 10, fontWeight: 500, lineHeight: 1.4 }}>{title}</p>
          <p style={{ fontSize: 32, fontWeight: 700, color: C.primary, margin: 0, lineHeight: 1.1 }}>
            {typeof value === "number" ? value.toLocaleString("ar-SA") : value}
          </p>
          {subtitle && <p style={{ fontSize: 12, color: C.textLight, margin: "6px 0 0" }}>{subtitle}</p>}
          {trend && (
            <p style={{
              display: "inline-flex", alignItems: "center", gap: 2,
              fontSize: 12, color: String(trend).startsWith("-") ? C.danger : C.success,
              margin: "8px 0 0", fontWeight: 700,
              padding: "2px 8px", borderRadius: 6,
              background: String(trend).startsWith("-") ? `${C.danger}10` : `${C.success}10`,
            }}>
              {String(trend).startsWith("+") || String(trend).startsWith("-") ? trend : `+${trend}`}
              {typeof trend === "number" || (typeof trend === "string" && /^[+-]?\d/.test(trend)) ? "%" : ""}
            </p>
          )}
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          background: `${color}15`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon size={22} color={color} />
        </div>
      </div>
    </Card>
  );
}

// ===== TabBar =====
export function TabBar({ tabs, active, onChange }) {
  // Tighten font/padding when many tabs to allow wrapping
  const many = tabs.length > 8;
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginBottom: 20,
      paddingBottom: 0,
      borderBottom: `1px solid ${C.border}`,
    }}>
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              padding: many ? "9px 14px" : "12px 18px",
              border: "none",
              borderBottom: isActive ? `3px solid ${C.teal}` : "3px solid transparent",
              background: isActive ? `${C.teal}08` : "transparent",
              color: isActive ? C.teal : C.textLight,
              fontFamily: "Readex Pro, sans-serif",
              fontWeight: isActive ? 700 : 500,
              fontSize: many ? 12 : 13,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
              borderRadius: "10px 10px 0 0",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ===== Modal =====
export function Modal({ title, onClose, children, width = 600 }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        animation: "fadeIn 0.2s",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: 20,
          width: "100%",
          maxWidth: width,
          maxHeight: "88vh",
          overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "scaleIn 0.2s ease-out",
        }}
      >
        <div style={{
          padding: "20px 32px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          background: C.white,
          zIndex: 1,
          borderRadius: "20px 20px 0 0",
        }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: C.primary, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex", alignItems: "center" }}>
            <X size={22} color={C.textLight} />
          </button>
        </div>
        <div style={{ padding: 32 }}>{children}</div>
      </div>
    </div>
  );
}

// ===== StatusBadge =====
export function StatusBadge({ status, statuses = TRANSACTION_STATUSES }) {
  const s = statuses.find((st) => st.key === status) || { label: status, color: C.textLight };
  return (
    <span style={{
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: `${s.color}18`,
      color: s.color,
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

// ===== EmptyState =====
export function EmptyState({ icon: Icon = FileText, title = "لا توجد بيانات", subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: 80, color: C.textLight }}>
      <Icon size={56} strokeWidth={1} style={{ marginBottom: 20, opacity: 0.35 }} />
      <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: C.primary }}>{title}</p>
      {subtitle && <p style={{ fontSize: 14, lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  );
}

// ===== DataTable =====
export function DataTable({ columns, data, onRowClick }) {
  if (!data || data.length === 0) return <EmptyState />;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.border}`, background: C.white }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={col.key} style={{
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: 700,
                color: C.textLight,
                borderBottom: `1px solid ${C.border}`,
                background: "#fafafa",
                whiteSpace: "nowrap",
                textAlign: "right",
                borderTopRightRadius: idx === 0 ? 12 : 0,
                borderTopLeftRadius: idx === columns.length - 1 ? 12 : 0,
              }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick && onRowClick(row)}
              style={{ cursor: onRowClick ? "pointer" : "default", transition: "background 0.12s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbfc")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {columns.map((col) => (
                <td key={col.key} style={{
                  padding: "14px 16px",
                  fontSize: 14,
                  color: C.text,
                  borderBottom: i === data.length - 1 ? "none" : `1px solid ${C.borderLight}`,
                  whiteSpace: col.nowrap ? "nowrap" : "normal",
                  lineHeight: 1.6,
                }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===== MeetingsTab =====
const SHARED_MEETING_TYPES = ["دوري", "غير دوري", "طارئ"];
const SHARED_MEETING_TYPE_COLORS = { "دوري": "#7c3aed", "غير دوري": C.teal, "طارئ": C.danger };
const SHARED_INITIAL_MEETING = {
  title: "", date: "", time: "", type: "دوري",
  link: "", attendees: "", absentees: "", agenda: "",
  minutes: "", minutesFile: "", notes: "",
};

export function MeetingsTab({ departmentName = "الإدارة" }) {
  const [meetings, setMeetings] = useState([
    { id: 1, title: `الاجتماع الدوري الأول — ${departmentName}`, date: "2026-01-15", time: "10:00", type: "دوري", link: "https://zoom.us/j/1234", attendees: ["مدير القسم", "نائب المدير", "المنسق"], absentees: [], agenda: "1. مراجعة الخطة\n2. تقرير الأداء", minutes: "تمت مراجعة الخطة واعتمادها بالإجماع. تم الاتفاق على متابعة المؤشرات شهرياً.", minutesFile: "محضر_01.pdf", notes: "", status: "completed" },
    { id: 2, title: `الاجتماع الدوري الثاني — ${departmentName}`, date: "2026-02-12", time: "10:00", type: "دوري", link: "", attendees: ["مدير القسم", "المنسق"], absentees: ["نائب المدير"], agenda: "1. مراجعة الميزانية\n2. تحديث الخطة", minutes: "تمت مراجعة الميزانية وتحديث الخطة بناءً على نتائج الربع الأول.", minutesFile: "محضر_02.pdf", notes: "", status: "completed" },
    { id: 3, title: `اجتماع طارئ — ${departmentName}`, date: "2026-03-05", time: "14:00", type: "طارئ", link: "", attendees: ["مدير القسم", "المدير التنفيذي"], absentees: [], agenda: "1. مناقشة الموضوع الطارئ", minutes: "تم اتخاذ القرارات اللازمة وتأجيل المشروع المؤقت.", minutesFile: "", notes: "اجتماع عاجل", status: "completed" },
    { id: 4, title: `الاجتماع الدوري الثالث — ${departmentName}`, date: "2026-04-09", time: "10:00", type: "دوري", link: "https://teams.microsoft.com/...", attendees: [], absentees: [], agenda: "1. ...", minutes: "", minutesFile: "", notes: "", status: "scheduled" },
  ]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(SHARED_INITIAL_MEETING);
  const [showToast, ToastView] = useToast();

  const splitList = s => s ? s.split(/،|,|\n/).map(x => x.trim()).filter(Boolean) : [];

  function handleSave() {
    if (!form.title || !form.date) {
      showToast("العنوان والتاريخ مطلوبان", "error");
      return;
    }
    setMeetings(prev => [...prev, {
      id: Date.now(),
      title: form.title,
      date: form.date,
      time: form.time || "",
      type: form.type,
      link: form.link || "",
      attendees: splitList(form.attendees),
      absentees: splitList(form.absentees),
      agenda: form.agenda || "",
      minutes: form.minutes || "",
      minutesFile: form.minutesFile || "",
      notes: form.notes || "",
      status: form.minutes ? "completed" : "scheduled",
    }]);
    setForm(SHARED_INITIAL_MEETING);
    setShowForm(false);
    showToast("تم حفظ الاجتماع");
  }

  const statusLabels = { completed: "منعقد", scheduled: "مجدول", cancelled: "ملغي" };
  const statusColors = { completed: C.success, scheduled: C.teal, cancelled: C.danger };

  return (
    <div>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.primary, margin: 0 }}>اجتماعات {departmentName}</h3>
        <Btn icon={Plus} onClick={() => setShowForm(true)}>اجتماع جديد</Btn>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {meetings.map((m) => (
          <Card key={m.id} onClick={() => setSelected(m)} style={{ cursor: "pointer", borderRight: `4px solid ${SHARED_MEETING_TYPE_COLORS[m.type] || statusColors[m.status]}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: 0 }}>{m.title}</h4>
                  {m.type && (
                    <span style={{
                      padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                      background: `${SHARED_MEETING_TYPE_COLORS[m.type] || C.teal}15`,
                      color: SHARED_MEETING_TYPE_COLORS[m.type] || C.teal,
                    }}>{m.type}</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 20, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={13} /> {m.date}</span>
                  {m.time && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={13} /> {m.time}</span>}
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={13} /> {(m.attendees || []).length} حضور</span>
                </div>
              </div>
              <span style={{
                padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                background: `${statusColors[m.status]}15`, color: statusColors[m.status],
              }}>
                {statusLabels[m.status]}
              </span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={650}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 16, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
              <span><Calendar size={13} /> {selected.date}</span>
              {selected.time && <span><Clock size={13} /> {selected.time}</span>}
              {selected.type && <span style={{ padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${SHARED_MEETING_TYPE_COLORS[selected.type]}15`, color: SHARED_MEETING_TYPE_COLORS[selected.type] }}>{selected.type}</span>}
            </div>

            {selected.link && (
              <div style={{ padding: 10, background: `${C.teal}08`, borderRadius: 8, fontSize: 13 }}>
                <strong style={{ color: C.teal }}>رابط الاجتماع: </strong>
                <a href={selected.link} target="_blank" rel="noopener noreferrer" dir="ltr" style={{ color: C.primary, wordBreak: "break-all" }}>{selected.link}</a>
              </div>
            )}

            {(selected.attendees || []).length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.success }}>الحضور ({selected.attendees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.attendees.join("، ")}</p>
              </div>
            )}

            {(selected.absentees || []).length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.danger }}>الغياب ({selected.absentees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.absentees.join("، ")}</p>
              </div>
            )}

            {selected.agenda && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>جدول الأعمال:</p>
                <p style={{ margin: "4px 0 0", padding: 12, background: C.bg, borderRadius: 8, fontSize: 14, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.agenda}</p>
              </div>
            )}

            {selected.minutes && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>محضر الاجتماع:</p>
                <div style={{ margin: "4px 0 0", padding: 14, background: `${C.teal}06`, border: `1px solid ${C.teal}30`, borderRadius: 10, fontSize: 14, color: C.text, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{selected.minutes}</div>
              </div>
            )}

            {selected.minutesFile && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: `${C.accent}10`, borderRadius: 10 }}>
                <Paperclip size={16} color={C.accent} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>مرفق المحضر:</span>
                <span style={{ fontSize: 13, color: C.text }}>{selected.minutesFile}</span>
              </div>
            )}

            {selected.notes && (
              <p style={{ margin: 0, padding: 10, background: C.bg, borderRadius: 8, fontSize: 13, color: C.textLight }}>
                <strong>ملاحظات:</strong> {selected.notes}
              </p>
            )}

            <Btn variant="ghost" icon={ArrowLeft} onClick={() => setSelected(null)}>إغلاق</Btn>
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal title="جدولة اجتماع جديد" onClose={() => { setShowForm(false); setForm(SHARED_INITIAL_MEETING); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان الاجتماع" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
              <Field label="الوقت" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
              <Field label="نوع الاجتماع" type="select" options={SHARED_MEETING_TYPES} value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
            </div>
            <Field label="رابط الاجتماع الإلكتروني" placeholder="https://zoom.us/..." value={form.link} onChange={(v) => setForm({ ...form, link: v })} />
            <Field label="الحضور (مفصولة بفواصل)" type="textarea" rows={2} placeholder="اسم1، اسم2، اسم3..." value={form.attendees} onChange={(v) => setForm({ ...form, attendees: v })} />
            <Field label="الغياب (مفصولة بفواصل)" type="textarea" rows={2} placeholder="اسم1، اسم2..." value={form.absentees} onChange={(v) => setForm({ ...form, absentees: v })} />
            <Field label="جدول الأعمال" type="textarea" rows={4} placeholder="1. ...&#10;2. ...&#10;3. ..." value={form.agenda} onChange={(v) => setForm({ ...form, agenda: v })} />
            <Field label="محضر الاجتماع" type="textarea" rows={5} placeholder="نص محضر الاجتماع..." value={form.minutes} onChange={(v) => setForm({ ...form, minutes: v })} />
            <div>
              <Field label="مرفق المحضر (اسم الملف)" placeholder="محضر.pdf" value={form.minutesFile} onChange={(v) => setForm({ ...form, minutesFile: v })} />
              <p style={{ margin: "-8px 0 0", fontSize: 11, color: C.textLight, fontStyle: "italic" }}>سيتم الرفع لاحقاً عند ربط التخزين</p>
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(SHARED_INITIAL_MEETING); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== TransactionsTab =====
export function TransactionsTab({ budget = 0 }) {
  const [filter, setFilter] = useState({ type: "", status: "", search: "" });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: "", type: "", description: "", beneficiary: "", amount: "", paymentMethod: "", invoiceNo: "", status: "pending", attachment: "", notes: "" });

  const demoTx = [
    { id: 1, date: "2026-01-10", type: "مشتريات", description: "شراء أجهزة حاسب محمول", beneficiary: "مؤسسة التقنية", amount: 12500, paymentMethod: "تحويل بنكي", invoiceNo: "INV-001", status: "paid", attachment: "فاتورة_001.pdf", notes: "" },
    { id: 2, date: "2026-01-15", type: "رواتب", description: "رواتب شهر محرم", beneficiary: "الموظفين", amount: 85000, paymentMethod: "تحويل بنكي", invoiceNo: "SAL-001", status: "paid", attachment: "كشف_رواتب.pdf", notes: "" },
    { id: 3, date: "2026-02-01", type: "إيجارات", description: "إيجار المقر الرئيسي — ربع أول", beneficiary: "مالك العقار", amount: 25000, paymentMethod: "شيك", invoiceNo: "RENT-001", status: "paid", attachment: "", notes: "دفعة ربع سنوية" },
    { id: 4, date: "2026-02-10", type: "طباعة ونشر", description: "طباعة كتيبات دعوية", beneficiary: "مطابع النور", amount: 8500, paymentMethod: "تحويل بنكي", invoiceNo: "PRT-001", status: "approval", attachment: "عرض_سعر.pdf", notes: "بانتظار اعتماد المدير" },
    { id: 5, date: "2026-02-20", type: "ضيافة", description: "ضيافة ملتقى بشائر الخير", beneficiary: "مؤسسة الضيافة", amount: 4200, paymentMethod: "نقدي", invoiceNo: "HSP-001", status: "pending", attachment: "", notes: "" },
    { id: 6, date: "2026-03-01", type: "صيانة", description: "صيانة مكيفات المبنى", beneficiary: "شركة التبريد", amount: 3800, paymentMethod: "تحويل بنكي", invoiceNo: "MNT-001", status: "paid", attachment: "تقرير_صيانة.pdf", notes: "" },
    { id: 7, date: "2026-03-15", type: "تقنية معلومات", description: "تجديد اشتراك البريد", beneficiary: "Google Workspace", amount: 2400, paymentMethod: "بطاقة ائتمان", invoiceNo: "IT-001", status: "paid", attachment: "", notes: "اشتراك سنوي" },
    { id: 8, date: "2026-03-20", type: "دعاية وإعلان", description: "حملة إعلانية — رمضان", beneficiary: "وكالة إعلانية", amount: 15000, paymentMethod: "تحويل بنكي", invoiceNo: "AD-001", status: "rejected", attachment: "عرض_سعر.pdf", notes: "تجاوز الميزانية المرصودة" },
  ];

  const [transactions, setTransactions] = useState(demoTx);

  const totalExpenses = transactions.filter((t) => t.status === "paid").reduce((s, t) => s + t.amount, 0);
  const remaining = budget - totalExpenses;

  const filtered = transactions.filter((t) => {
    if (filter.type && t.type !== filter.type) return false;
    if (filter.status && t.status !== filter.status) return false;
    if (filter.search && !t.description.includes(filter.search) && !t.beneficiary.includes(filter.search)) return false;
    return true;
  });

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20, marginBottom: 20 }}>
        <KPICard title="الميزانية المخصصة" value={budget.toLocaleString("ar-SA")} icon={DollarSign} color={C.primary} subtitle="ريال" />
        <KPICard title="إجمالي المصروفات" value={totalExpenses.toLocaleString("ar-SA")} icon={TrendingUp} color={C.danger} subtitle="ريال" />
        <KPICard title="المتبقي" value={remaining.toLocaleString("ar-SA")} icon={CreditCard} color={C.success} subtitle="ريال" />
        <KPICard title="عدد المعاملات" value={transactions.length} icon={Receipt} color={C.teal} />
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="بحث" value={filter.search} onChange={(v) => setFilter({ ...filter, search: v })} placeholder="بحث بالوصف أو المستفيد..." style={{ marginBottom: 0 }} />
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="النوع" type="select" value={filter.type} onChange={(v) => setFilter({ ...filter, type: v })} options={TRANSACTION_TYPES} style={{ marginBottom: 0 }} />
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="الحالة" type="select" value={filter.status} onChange={(v) => setFilter({ ...filter, status: v })} options={TRANSACTION_STATUSES.map((s) => ({ value: s.key, label: s.label }))} style={{ marginBottom: 0 }} />
          </div>
          <Btn icon={Plus} onClick={() => setShowForm(true)}>إضافة معاملة</Btn>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={[
            { key: "id", label: "#", render: (v) => <span style={{ fontWeight: 600, color: C.textLight }}>#{v}</span> },
            { key: "date", label: "التاريخ", nowrap: true },
            { key: "type", label: "النوع", render: (v) => <span style={{ padding: "2px 8px", background: `${C.primary}08`, borderRadius: 6, fontSize: 11, fontWeight: 500 }}>{v}</span> },
            { key: "description", label: "الوصف" },
            { key: "beneficiary", label: "المستفيد" },
            { key: "amount", label: "المبلغ", nowrap: true, render: (v) => <span style={{ fontWeight: 700, color: C.primary }}>{v.toLocaleString("ar-SA")} ر.س</span> },
            { key: "paymentMethod", label: "طريقة الدفع" },
            { key: "invoiceNo", label: "رقم الفاتورة", render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v}</span> },
            { key: "status", label: "الحالة", render: (v) => <StatusBadge status={v} /> },
            { key: "attachment", label: "مرفق", render: (v) => v ? <Paperclip size={14} color={C.teal} /> : <span style={{ color: "#ccc" }}>—</span> },
          ]}
          data={filtered}
        />
      </Card>

      {/* Add Form */}
      {showForm && (
        <Modal title="إضافة معاملة جديدة" onClose={() => setShowForm(false)} width={700}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="التاريخ" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
            <Field label="النوع" type="select" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TRANSACTION_TYPES} required />
            <Field label="المبلغ" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} required />
            <Field label="طريقة الدفع" type="select" value={form.paymentMethod} onChange={(v) => setForm({ ...form, paymentMethod: v })} options={PAYMENT_METHODS} />
            <Field label="المستفيد" value={form.beneficiary} onChange={(v) => setForm({ ...form, beneficiary: v })} required />
            <Field label="رقم الفاتورة" value={form.invoiceNo} onChange={(v) => setForm({ ...form, invoiceNo: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={TRANSACTION_STATUSES.map((s) => ({ value: s.key, label: s.label }))} />
            <Field label="اسم المرفق" value={form.attachment} onChange={(v) => setForm({ ...form, attachment: v })} placeholder="اسم الملف" />
          </div>
          <Field label="الوصف" value={form.description} onChange={(v) => setForm({ ...form, description: v })} required />
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} rows={2} />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Btn icon={Save} onClick={() => {
              setTransactions([...transactions, { ...form, id: transactions.length + 1, amount: Number(form.amount) }]);
              setShowForm(false);
              setForm({ date: "", type: "", description: "", beneficiary: "", amount: "", paymentMethod: "", invoiceNo: "", status: "pending", attachment: "", notes: "" });
            }}>حفظ</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>إلغاء</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ===== OperationalPlanTab — Rich Goals + Tasks =====
const TASK_STATUS_META = {
  not_started: { label: "لم يبدأ", color: C.textLight },
  in_progress: { label: "جاري", color: C.warning },
  completed: { label: "مكتمل", color: C.success },
  delayed: { label: "متأخر", color: C.danger },
};
const TASK_STATUS_OPTIONS = [
  { value: "not_started", label: "لم يبدأ" },
  { value: "in_progress", label: "جاري" },
  { value: "completed", label: "مكتمل" },
  { value: "delayed", label: "متأخر" },
];
const RATING_OPTIONS = ["", "ممتاز", "جيد", "مقبول", "ضعيف"];
const PRIORITY_OPTIONS = [
  { value: "high", label: "عالية", color: C.danger },
  { value: "medium", label: "متوسطة", color: C.warning },
  { value: "low", label: "منخفضة", color: C.teal },
];

function opGoalProgress(goal) {
  if (!goal.tasks || goal.tasks.length === 0) return 0;
  const done = goal.tasks.filter(t => t.status === "completed").length;
  return Math.round((done / goal.tasks.length) * 100);
}

const INITIAL_OP_GOAL = {
  name: "", description: "", owner: "", priority: "medium",
  startDate: "", endDate: "", weight: "", indicators: "", budget: "",
};
const INITIAL_OP_TASK = {
  title: "", description: "", owner: "",
  startDate: "", deadline: "",
  status: "not_started", rating: "", notes: "",
};

export function OperationalPlanTab({ departmentKey }) {
  const initial = STRATEGIC_PLAN[departmentKey];
  const [goals, setGoals] = useState(initial?.goals || []);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showTaskFormFor, setShowTaskFormFor] = useState(null);
  const [goalForm, setGoalForm] = useState(INITIAL_OP_GOAL);
  const [taskForm, setTaskForm] = useState(INITIAL_OP_TASK);
  const [showToast, ToastView] = useToast();

  if (!initial) return <EmptyState title="لا توجد خطة تشغيلية لهذه الإدارة" />;

  // KPIs
  const totalGoals = goals.length;
  const achievedGoals = goals.filter(g => opGoalProgress(g) === 100).length;
  const inProgressGoals = goals.filter(g => {
    const p = opGoalProgress(g);
    return p > 0 && p < 100;
  }).length;
  const totalWeight = goals.reduce((s, g) => s + (Number(g.weight) || 0), 0) || 1;
  const planProgress = Math.round(
    goals.reduce((s, g) => s + opGoalProgress(g) * (Number(g.weight) || 0), 0) / totalWeight
  );

  function toggleTaskComplete(goalId, taskId) {
    setGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        tasks: g.tasks.map(t => {
          if (t.id !== taskId) return t;
          return { ...t, status: t.status === "completed" ? "in_progress" : "completed" };
        }),
      };
    }));
  }

  function handleSaveGoal() {
    if (!goalForm.name) {
      showToast("اسم الهدف مطلوب", "error");
      return;
    }
    setGoals(prev => [...prev, {
      id: `g${Date.now()}`,
      name: goalForm.name,
      description: goalForm.description,
      owner: goalForm.owner,
      priority: goalForm.priority,
      startDate: goalForm.startDate,
      endDate: goalForm.endDate,
      weight: Number(goalForm.weight) || 0,
      indicators: goalForm.indicators,
      budget: Number(goalForm.budget) || 0,
      tasks: [],
    }]);
    setGoalForm(INITIAL_OP_GOAL);
    setShowGoalForm(false);
    showToast("تمت إضافة الهدف");
  }

  function handleSaveTask() {
    if (!taskForm.title) {
      showToast("عنوان المهمة مطلوب", "error");
      return;
    }
    setGoals(prev => prev.map(g => {
      if (g.id !== showTaskFormFor) return g;
      return {
        ...g,
        tasks: [...g.tasks, {
          id: `t${Date.now()}`,
          title: taskForm.title,
          description: taskForm.description,
          owner: taskForm.owner,
          startDate: taskForm.startDate,
          deadline: taskForm.deadline,
          status: taskForm.status,
          rating: taskForm.rating,
          notes: taskForm.notes,
        }],
      };
    }));
    setTaskForm(INITIAL_OP_TASK);
    setShowTaskFormFor(null);
    showToast("تمت إضافة المهمة");
  }

  return (
    <div>
      {ToastView}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: 0 }}>
          الخطة التشغيلية — {initial.name}
        </h3>
        <Btn icon={Plus} onClick={() => setShowGoalForm(true)} size="sm">إضافة هدف جديد</Btn>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        <KPICard title="عدد الأهداف" value={totalGoals} icon={Target} color={C.teal} />
        <KPICard title="الأهداف المحققة" value={achievedGoals} icon={CheckCircle} color={C.success} />
        <KPICard title="الأهداف الجارية" value={inProgressGoals} icon={Clock} color={C.warning} />
        <KPICard title="نسبة التحقق الإجمالية" value={`${planProgress}%`} icon={TrendingUp} color={C.primary} />
      </div>

      {/* Goals */}
      <div style={{ display: "grid", gap: 14 }}>
        {goals.map(g => {
          const prog = opGoalProgress(g);
          const isAchieved = prog === 100;
          const goalColor = isAchieved ? C.success : prog >= 50 ? C.teal : C.warning;
          const completedTasks = g.tasks.filter(t => t.status === "completed").length;
          const priorityMeta = PRIORITY_OPTIONS.find(p => p.value === g.priority) || PRIORITY_OPTIONS[1];

          return (
            <Card key={g.id} style={{ padding: 18, borderRight: `4px solid ${goalColor}` }}>
              {/* Goal header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 250 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{g.name}</h4>
                    {isAchieved && (
                      <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${C.success}15`, color: C.success }}>
                        ✓ متحقق
                      </span>
                    )}
                    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${priorityMeta.color}15`, color: priorityMeta.color }}>
                      {priorityMeta.label}
                    </span>
                  </div>
                  {g.description && (
                    <p style={{ margin: 0, fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{g.description}</p>
                  )}
                </div>
                <div style={{ textAlign: "left", minWidth: 130 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: goalColor }}>{prog}%</div>
                  <div style={{ fontSize: 11, color: C.textLight }}>{completedTasks}/{g.tasks.length} مهام</div>
                </div>
              </div>

              {/* Meta */}
              {(g.owner || g.startDate || g.endDate || g.budget > 0) && (
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: C.textLight, marginBottom: 8 }}>
                  {g.owner && <span><strong style={{ color: C.primary }}>المسؤول:</strong> {g.owner}</span>}
                  {g.startDate && <span><strong style={{ color: C.primary }}>البداية:</strong> {g.startDate}</span>}
                  {g.endDate && <span><strong style={{ color: C.primary }}>النهاية:</strong> {g.endDate}</span>}
                  {g.budget > 0 && <span><strong style={{ color: C.primary }}>الميزانية:</strong> {g.budget.toLocaleString()} ر.س</span>}
                </div>
              )}

              {g.indicators && (
                <div style={{ padding: 10, background: `${C.teal}06`, borderRadius: 8, marginBottom: 10 }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.teal }}>مؤشرات الأداء:</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{g.indicators}</p>
                </div>
              )}

              {/* Progress bar */}
              <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ height: "100%", width: `${prog}%`, background: goalColor, transition: "width 0.4s" }} />
              </div>

              {/* Tasks table */}
              {g.tasks.length === 0 ? (
                <p style={{ fontSize: 12, color: C.textLight, margin: 0 }}>لا توجد مهام بعد.</p>
              ) : (
                <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 36 }}>✓</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}` }}>المهمة</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 120 }}>المسؤول</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 110 }}>البداية</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 110 }}>الموعد</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 90 }}>الحالة</th>
                        <th style={{ padding: "8px 10px", background: "#fafafa", textAlign: "right", fontWeight: 700, color: C.textLight, borderBottom: `1px solid ${C.border}`, width: 80 }}>التقييم</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.tasks.map(t => {
                        const meta = TASK_STATUS_META[t.status] || TASK_STATUS_META.not_started;
                        return (
                          <tr key={t.id}>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}`, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={t.status === "completed"}
                                onChange={() => toggleTaskComplete(g.id, t.id)}
                                title="تأشير كمكتمل"
                              />
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
                              <span style={{
                                fontWeight: 600, color: C.primary,
                                textDecoration: t.status === "completed" ? "line-through" : "none",
                                opacity: t.status === "completed" ? 0.6 : 1,
                              }}>{t.title}</span>
                              {t.notes && <p style={{ margin: "2px 0 0", fontSize: 11, color: C.textLight }}>{t.notes}</p>}
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.owner || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.startDate || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.deadline || "—"}</td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>
                              <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${meta.color}15`, color: meta.color }}>
                                {meta.label}
                              </span>
                            </td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.borderLight}` }}>{t.rating || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                <Btn size="sm" variant="ghost" icon={Plus} onClick={() => { setShowTaskFormFor(g.id); setTaskForm(INITIAL_OP_TASK); }}>
                  إضافة مهمة
                </Btn>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Add Goal Modal */}
      {showGoalForm && (
        <Modal title="إضافة هدف جديد" onClose={() => { setShowGoalForm(false); setGoalForm(INITIAL_OP_GOAL); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الهدف" required placeholder="مثال: زيادة عدد المستفيدين 20%" value={goalForm.name} onChange={v => setGoalForm({ ...goalForm, name: v })} />
            <Field label="وصف الهدف" type="textarea" rows={3} placeholder="وصف تفصيلي للهدف..." value={goalForm.description} onChange={v => setGoalForm({ ...goalForm, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المسؤول عن الهدف" placeholder="اسم المسؤول" value={goalForm.owner} onChange={v => setGoalForm({ ...goalForm, owner: v })} />
              <Field label="الأولوية" type="select" options={PRIORITY_OPTIONS} value={goalForm.priority} onChange={v => setGoalForm({ ...goalForm, priority: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={goalForm.startDate} onChange={v => setGoalForm({ ...goalForm, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" value={goalForm.endDate} onChange={v => setGoalForm({ ...goalForm, endDate: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الوزن / النسبة من إجمالي الخطة (%)" type="number" placeholder="مثال: 25" value={goalForm.weight} onChange={v => setGoalForm({ ...goalForm, weight: v })} />
              <Field label="الميزانية المخصصة (ر.س)" type="number" placeholder="0" value={goalForm.budget} onChange={v => setGoalForm({ ...goalForm, budget: v })} />
            </div>
            <Field label="مؤشرات الأداء" type="textarea" rows={3} placeholder="مؤشر 1&#10;مؤشر 2&#10;مؤشر 3" value={goalForm.indicators} onChange={v => setGoalForm({ ...goalForm, indicators: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSaveGoal}>حفظ الهدف</Btn>
              <Btn variant="ghost" onClick={() => { setShowGoalForm(false); setGoalForm(INITIAL_OP_GOAL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Task Modal */}
      {showTaskFormFor && (
        <Modal title="إضافة مهمة" onClose={() => { setShowTaskFormFor(null); setTaskForm(INITIAL_OP_TASK); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان المهمة" required placeholder="عنوان المهمة" value={taskForm.title} onChange={v => setTaskForm({ ...taskForm, title: v })} />
            <Field label="وصف المهمة" type="textarea" rows={2} placeholder="وصف..." value={taskForm.description} onChange={v => setTaskForm({ ...taskForm, description: v })} />
            <Field label="المسؤول" placeholder="اسم المسؤول" value={taskForm.owner} onChange={v => setTaskForm({ ...taskForm, owner: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={taskForm.startDate} onChange={v => setTaskForm({ ...taskForm, startDate: v })} />
              <Field label="الموعد النهائي" type="date" value={taskForm.deadline} onChange={v => setTaskForm({ ...taskForm, deadline: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الحالة" type="select" options={TASK_STATUS_OPTIONS} value={taskForm.status} onChange={v => setTaskForm({ ...taskForm, status: v })} />
              <Field label="التقييم (اختياري)" type="select" options={RATING_OPTIONS.map(r => ({ value: r, label: r || "—" }))} value={taskForm.rating} onChange={v => setTaskForm({ ...taskForm, rating: v })} />
            </div>
            <Field label="الملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={taskForm.notes} onChange={v => setTaskForm({ ...taskForm, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSaveTask}>حفظ المهمة</Btn>
              <Btn variant="ghost" onClick={() => { setShowTaskFormFor(null); setTaskForm(INITIAL_OP_TASK); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ===== ReportsTab =====
// Per-department report type options + extra fields
const REPORT_TYPES_BASE = ["شهري", "ربع سنوي", "نصف سنوي", "سنوي", "خاص"];
const REPORT_STATUS_OPTIONS = ["مسودة", "مراجعة", "معتمد"];
const REPORT_STATUS_LABEL_TO_KEY = { "مسودة": "draft", "مراجعة": "review", "معتمد": "approved" };
const REPORT_EXTRA_FIELDS = {
  "الجاليات": [
    { key: "beneficiaries", label: "عدد المستفيدين", type: "number" },
    { key: "activePreachers", label: "عدد الدعاة النشطين", type: "number" },
    { key: "programsCount", label: "عدد البرامج", type: "number" },
    { key: "newMuslims", label: "عدد المسلمين الجدد", type: "number" },
    { key: "achievements", label: "أبرز الإنجازات", type: "textarea" },
  ],
  "المساجد": [
    { key: "mosquesServed", label: "عدد المساجد المخدومة", type: "number" },
    { key: "maintenanceDone", label: "عمليات الصيانة المنجزة", type: "number" },
    { key: "totalCost", label: "التكلفة الإجمالية (ر.س)", type: "number" },
    { key: "pendingIssues", label: "المشاكل المعلقة", type: "textarea" },
  ],
  "القسم النسائي": [
    { key: "beneficiaries", label: "عدد المستفيدات", type: "number" },
    { key: "circles", label: "عدد الحلقات", type: "number" },
    { key: "attendanceRate", label: "معدل الحضور (%)", type: "number" },
    { key: "topPrograms", label: "أبرز البرامج", type: "textarea" },
  ],
  "المالية": [
    { key: "totalIncome", label: "إجمالي الإيرادات (ر.س)", type: "number" },
    { key: "totalExpense", label: "إجمالي المصروفات (ر.س)", type: "number" },
    { key: "netResult", label: "الصافي (ر.س)", type: "number" },
    { key: "topExpenses", label: "أكبر بنود الصرف", type: "textarea" },
  ],
  "المتبرعين": [
    { key: "newDonors", label: "عدد المتبرعين الجدد", type: "number" },
    { key: "totalDonations", label: "إجمالي التبرعات (ر.س)", type: "number" },
    { key: "topDonor", label: "أكبر متبرع", type: "text" },
    { key: "retentionRate", label: "نسبة الاحتفاظ (%)", type: "number" },
  ],
  "الموارد البشرية": [
    { key: "employeesCount", label: "عدد الموظفين", type: "number" },
    { key: "newHires", label: "التعيينات الجديدة", type: "number" },
    { key: "resignations", label: "الاستقالات", type: "number" },
    { key: "trainingHours", label: "ساعات التدريب", type: "number" },
  ],
  "التطوع": [
    { key: "volunteersCount", label: "عدد المتطوعين", type: "number" },
    { key: "totalHours", label: "ساعات التطوع", type: "number" },
    { key: "eventsExecuted", label: "الفعاليات المنفذة", type: "number" },
  ],
  "الاتصال المؤسسي": [
    { key: "newsPublished", label: "عدد الأخبار المنشورة", type: "number" },
    { key: "followerGrowth", label: "نمو المتابعين (%)", type: "number" },
    { key: "mediaCoverage", label: "التغطيات الإعلامية", type: "number" },
  ],
  "الحوكمة": [
    { key: "governanceScore", label: "درجة الحوكمة (%)", type: "number" },
    { key: "closedIssues", label: "الملاحظات المغلقة", type: "number" },
    { key: "renewedLicenses", label: "التراخيص المجددة", type: "number" },
  ],
  "المجلس": [
    { key: "meetingsHeld", label: "عدد الاجتماعات", type: "number" },
    { key: "decisionsTaken", label: "القرارات المتخذة", type: "number" },
    { key: "decisionsImplemented", label: "القرارات المنفذة", type: "number" },
  ],
};

export function ReportsTab({ departmentName = "الإدارة" }) {
  const [reports, setReports] = useState([
    { id: 1, title: `التقرير الشهري — ${departmentName} — محرم 1447`, date: "2026-01-30", type: "شهري", status: "approved", periodFrom: "2026-01-01", periodTo: "2026-01-30", preparedBy: "مدير القسم" },
    { id: 2, title: `التقرير الشهري — ${departmentName} — صفر 1447`, date: "2026-02-28", type: "شهري", status: "approved", periodFrom: "2026-02-01", periodTo: "2026-02-28", preparedBy: "مدير القسم" },
    { id: 3, title: `تقرير الربع الأول — ${departmentName}`, date: "2026-03-30", type: "ربع سنوي", status: "draft", periodFrom: "2026-01-01", periodTo: "2026-03-30", preparedBy: "مدير القسم" },
  ]);
  const extraFields = REPORT_EXTRA_FIELDS[departmentName] || [];
  const initialExtras = Object.fromEntries(extraFields.map(f => [f.key, ""]));
  const INITIAL_REPORT_FORM = {
    title: "", periodFrom: "", periodTo: "", type: "شهري",
    summary: "", recommendations: "", preparedBy: "", statusLabel: "مسودة",
    ...initialExtras,
  };
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_REPORT_FORM);
  const [showToast, ToastView] = useToast();

  const statusLabels = { approved: "معتمد", draft: "مسودة", review: "مراجعة" };
  const statusColors = { approved: C.success, draft: C.textLight, review: C.accent };

  function handleSave() {
    if (!form.title || !form.periodFrom || !form.periodTo) {
      showToast("العنوان والفترة الزمنية مطلوبة", "error");
      return;
    }
    const newR = {
      id: Date.now(),
      title: form.title,
      date: new Date().toISOString().slice(0, 10),
      type: form.type,
      status: REPORT_STATUS_LABEL_TO_KEY[form.statusLabel] || "draft",
      periodFrom: form.periodFrom,
      periodTo: form.periodTo,
      summary: form.summary,
      recommendations: form.recommendations,
      preparedBy: form.preparedBy || "—",
      extras: Object.fromEntries(extraFields.map(f => [f.key, form[f.key]])),
    };
    setReports(prev => [newR, ...prev]);
    setForm(INITIAL_REPORT_FORM);
    setShowForm(false);
    showToast("تم إنشاء التقرير");
  }

  return (
    <div>
      {ToastView}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.primary, margin: 0 }}>تقارير {departmentName}</h3>
        <Btn icon={Plus} onClick={() => setShowForm(true)}>إنشاء تقرير جديد</Btn>
      </div>
      <div style={{ display: "grid", gap: 14 }}>
        {reports.map((r) => (
          <Card key={r.id} onClick={() => setSelected(r)} style={{ cursor: "pointer", borderRight: `4px solid ${statusColors[r.status]}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: C.primary, margin: 0, marginBottom: 6 }}>{r.title}</h4>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: C.textLight }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={13} /> {r.date}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FileText size={13} /> {r.type}</span>
                  {r.preparedBy && <span>المعد: {r.preparedBy}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{
                  padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  background: `${statusColors[r.status]}15`, color: statusColors[r.status],
                }}>
                  {statusLabels[r.status]}
                </span>
                <Btn variant="ghost" size="sm" icon={Download} onClick={e => { e.stopPropagation(); showToast("بدأ التحميل (تجريبي)"); }}>تحميل</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 13 }}>
              <div><strong style={{ color: C.textLight }}>النوع:</strong> {selected.type}</div>
              <div><strong style={{ color: C.textLight }}>الفترة:</strong> {selected.periodFrom} → {selected.periodTo}</div>
              <div><strong style={{ color: C.textLight }}>الحالة:</strong> {statusLabels[selected.status]}</div>
            </div>
            {selected.preparedBy && <div style={{ fontSize: 13 }}><strong>المعد:</strong> {selected.preparedBy}</div>}
            {selected.summary && (
              <div>
                <strong style={{ color: C.primary }}>الملخص التنفيذي:</strong>
                <p style={{ margin: "4px 0 0", padding: 12, background: C.bg, borderRadius: 8, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.summary}</p>
              </div>
            )}
            {selected.recommendations && (
              <div>
                <strong style={{ color: C.primary }}>التوصيات:</strong>
                <p style={{ margin: "4px 0 0", padding: 12, background: `${C.teal}06`, borderRadius: 8, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.recommendations}</p>
              </div>
            )}
            {selected.extras && Object.keys(selected.extras).length > 0 && (
              <div>
                <strong style={{ color: C.primary }}>بيانات القسم:</strong>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                  {extraFields.map(f => selected.extras[f.key] ? (
                    <div key={f.key} style={{ padding: 10, background: C.bg, borderRadius: 8, fontSize: 13 }}>
                      <span style={{ color: C.textLight }}>{f.label}:</span> <strong>{selected.extras[f.key]}</strong>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal title={`إنشاء تقرير — ${departmentName}`} onClose={() => { setShowForm(false); setForm(INITIAL_REPORT_FORM); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان التقرير" required value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" required value={form.periodFrom} onChange={v => setForm({ ...form, periodFrom: v })} />
              <Field label="إلى تاريخ" type="date" required value={form.periodTo} onChange={v => setForm({ ...form, periodTo: v })} />
              <Field label="نوع التقرير" type="select" options={REPORT_TYPES_BASE} value={form.type} onChange={v => setForm({ ...form, type: v })} />
            </div>

            {/* Department-specific extra fields */}
            {extraFields.length > 0 && (
              <div style={{ padding: 14, background: `${C.teal}06`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
                <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal }}>
                  بيانات قسم {departmentName}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: extraFields.some(f => f.type === "textarea") ? "1fr" : "1fr 1fr", gap: 10 }}>
                  {extraFields.map(f => (
                    <Field
                      key={f.key}
                      label={f.label}
                      type={f.type}
                      rows={f.type === "textarea" ? 2 : undefined}
                      value={form[f.key] || ""}
                      onChange={v => setForm({ ...form, [f.key]: v })}
                    />
                  ))}
                </div>
              </div>
            )}

            <Field label="الملخص التنفيذي" type="textarea" rows={4} value={form.summary} onChange={v => setForm({ ...form, summary: v })} />
            <Field label="التوصيات" type="textarea" rows={3} value={form.recommendations} onChange={v => setForm({ ...form, recommendations: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المعد" placeholder="اسم معد التقرير" value={form.preparedBy} onChange={v => setForm({ ...form, preparedBy: v })} />
              <Field label="الحالة" type="select" options={REPORT_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Save} onClick={handleSave}>حفظ التقرير</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(INITIAL_REPORT_FORM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== ColorTemplateSelector =====
export function ColorTemplateSelector({ onSelect, selected }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
      {COLOR_TEMPLATES.map((t, i) => (
        <div
          key={i}
          onClick={() => onSelect(t)}
          style={{
            padding: 12,
            borderRadius: 10,
            border: `2px solid ${selected?.name === t.name ? C.teal : C.border}`,
            cursor: "pointer",
            textAlign: "center",
            transition: "all 0.2s",
            background: selected?.name === t.name ? `${C.teal}08` : C.white,
          }}
        >
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: t.primary }} />
            <div style={{ width: 28, height: 28, borderRadius: 6, background: t.secondary, border: `1px solid ${C.border}` }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.text }}>{t.name}</span>
        </div>
      ))}
    </div>
  );
}
